let { splitOnPipes, detectTextDirection } = require('../../shared/simple-helpers');
let chalk = require('chalk');

function parseText(options) {
  return require('./parse_text')(options); // avoid circular dependency
}

function TextToken(text) {
  this.text = text;
  this.type = 'text';
}

function LocalReferenceToken(
  targetTopic,
  targetSubtopic,
  enclosingTopic,
  enclosingSubtopic,
  text,
  parserContext
) {
  this.text = text;
  this.type = 'local';
  this.tokens = parseText({ text: convertSpacesToHtml(text), parserContext: parserContext.clone({ insideToken: true }) });
  this.targetSubtopic = targetSubtopic;
  this.targetTopic = targetTopic;
  this.enclosingTopic = enclosingTopic;
  this.enclosingSubtopic = enclosingSubtopic;
  parserContext.paragraphReferences.push(this); // not necessary for import validation but included for consistency
}

function GlobalReferenceToken(
  pathString,
  enclosingTopic,
  enclosingSubtopic,
  text,
  parserContext
) {
  this.text = text;
  this.type = 'global';
  this.tokens = parseText({ text: convertSpacesToHtml(text), parserContext: parserContext.clone({ insideToken: true }) });
  this.pathString = pathString;
  this.enclosingTopic = enclosingTopic;
  this.enclosingSubtopic = enclosingSubtopic;
  parserContext.paragraphReferences.push(this);
}

function DisabledReferenceToken(
  text,
  enclosingTopic,
  enclosingSubtopic,
  parserContext
) {
  this.text = text;
  this.type = 'disabled_reference';
  this.tokens = parseText({ text: convertSpacesToHtml(text), parserContext: parserContext.clone({ insideToken: true }) });
  this.enclosingTopic = enclosingTopic;
  this.enclosingSubtopic = enclosingSubtopic;
}

function FragmentReferenceToken(
  text,
  parserContext
) {
  this.text = text;
  this.type = 'fragment_reference';
  this.tokens = parseText({ text: convertSpacesToHtml(text), parserContext: parserContext.clone({ insideToken: true }) });
}

function convertSpacesToHtml(str) { // eg [[ abc ]] -> [[&nbsp;abc&nbsp;]]
  return str.replace(/^ +| +$/g, match => '&nbsp;'.repeat(match.length));
}

function ExternalLinkToken(url, text, parserContext, options = {}) {
  this.type = 'external';
  this.url = projectPathUrl(
    (url || text).replace(/\\\\|\\./g, match => match === '\\\\' ? '\\' : match[1]),
    parserContext?.projectPathPrefix
  );
  this.text = options.iconOnly ? '' : (text || url);
  if (options.iconOnly) this.iconOnly = true;
  if (options.iconOnly) {
    this.tokens = [];
  } else if (!text) {
    this.tokens = [{ type: 'text', text: url }]; // to avoid infinite loop of URL recognition
  } else {
    this.tokens = parseText({ text: text || url, parserContext: parserContext.clone({ insideToken: true }) });
  }
}

function projectPathUrl(url, projectPathPrefix) {
  const hasScheme = /^[A-Za-z][A-Za-z0-9+.-]*:/.test(url);
  const isProtocolRelative = url.startsWith('//');
  if (hasScheme || isProtocolRelative) return url;

  const prefix = (projectPathPrefix || '').replace(/^\/+|\/+$/g, '');
  const relativePath = url.replace(/^\/+/, '');
  return `/${prefix ? `${prefix}/` : ''}${relativePath}`;
}

function ImageToken({ alt, resourceUrl, title, caption, anchorUrl, parserContext }) {
  this.type = 'image';
  let { projectPathPrefix } = parserContext;
  if (resourceUrl.startsWith('/') && projectPathPrefix) {
    resourceUrl = `/${projectPathPrefix}${resourceUrl}`;
  }
  this.resourceUrl = resourceUrl;
  this.title = (title||'').split('\\\\').map(s => s.replace(/\\/g, '')).join('') || null; // title is not tokenized so escaping must be done manually
  this.tokens = parseText({
    text: caption || '',
    parserContext: parserContext.clone({ insideToken: true }) });
  this.altText = alt || null;
  this.caption = caption;
  this.anchorUrl = anchorUrl || null;
}

function FootnoteMarkerToken(superscript) {
  this.type = 'footnote_marker';
  this.text = superscript;
}

function replaceHtmlInsertions(html, replaceInsertion) {
  let result = '';
  let cursor = 0;
  let start = null;
  let depth = 0;

  for (const match of html.matchAll(/(^|[^\\])(\{\{|}})/g)) {
    const delimiter = match[2];
    const index = match.index + match[1].length;

    if (delimiter === '{{') {
      if (depth++ === 0) {
        result += html.slice(cursor, index);
        start = index;
      }
    } else if (depth && --depth === 0) {
      result += replaceInsertion(html.slice(start + 2, index), start, start + 2);
      cursor = index + 2;
      start = null;
    }
  }

  return result + html.slice(depth ? start : cursor);
}

function HtmlToken(html, parserContext) {
  this.type = 'html_element';
  this.tokenInsertions = [];

  this.html = replaceHtmlInsertions(html, (content, offset, contentOffset) => {
    this.tokenInsertions.push(
      parseText({
        text: content,
        parserContext: parserContext.clone({ insideToken: true })
          .incrementLineAndResetCharacterNumber(html.slice(0, offset).match(/\n/g)?.length || 0)
          .incrementCharacterNumber(html.slice(0, contentOffset).split('\n').slice(-1)[0].length)
      })
    );

    return `<div class="canopy-html-insertion" data-replacement-number="${this.tokenInsertions.length - 1}"></div>`;
  });
}

function CodeBlockToken(text) {
  this.type = 'code_block';
  this.text = text.split('\\\\').map(s => s.replace('\\', '')).join('\\'); // remove single backslashes, and replace double with single
}

function BlockQuoteToken(text, direction, parserContext) {
  this.type = 'block_quote';

  this.tokens = parseText({
    text,
    parserContext: parserContext.clone({
      insideToken: true,
      linePrefixSize: 2 // ie "> "
    }).incrementCharacterNumber('> '.length)
  });

  this.direction = direction;
}

function ListToken(text, parserContext) {
  this.topLevelNodes = [];
  this.type = 'list';

  text.split("\n").filter(Boolean).forEach((line, lineIndex) => { // filter handles terminal newline
    let initialWhitespace = line.match(/^(\s*)/)[1];
    let orderedListMatch = line.match(/^\s*(\S+)(\.\s?)(.*$)/);
    let unorderedListMatch = line.match(/^\s*([+*-])(\s?)(.*$)/);
    let match = orderedListMatch || unorderedListMatch;

    let ordinal = match[1];
    let postOrdinalCharacters = match[2];
    let lineContents = match[3];
    let tokensOfLine = parseText({
      text: lineContents,
      parserContext: parserContext.clone({
        insideToken: true
      }).incrementLineAndResetCharacterNumber(lineIndex).incrementCharacterNumber(initialWhitespace.length + ordinal.length + postOrdinalCharacters.length)
    });

    let newNode = {
      indentation: initialWhitespace.length,
      ordinal: ordinal,
      ordered: !!orderedListMatch,
      tokensOfLine: tokensOfLine,
      children: [],
      parentNode: null
    };

    if (!this.lastNode) {
      this.topLevelNodes.push(newNode); // This is the first node we're parsing
    } else if (newNode.indentation > this.lastNode.indentation) { // Child node of the previous line
      newNode.parentNode = this.lastNode;
      this.lastNode.children.push(newNode);
    } else if (newNode.indentation === this.lastNode.indentation) { // Sibling node of the previous line
      if (newNode.indentation === 0) {
        this.topLevelNodes.push(newNode);
      } else {
        newNode.parentNode = this.lastNode.parentNode;
        this.lastNode.parentNode.children.push(newNode);
      }
    } else { // Child node of a previous line, or new top-level node
      let parentNode = this.lastNode;
      while (parentNode && newNode.indentation <= parentNode.indentation) {
        parentNode = parentNode.parentNode;
      }
      if (!parentNode) {
        this.topLevelNodes.push(newNode);
        newNode.parentNode = null;
      } else {
        newNode.parentNode = parentNode;
        parentNode.children.push(newNode);
      }
    }

    this.lastNode = newNode;
  });

  removeCircularListKeys([this]);

  function removeCircularListKeys(tokens) {
    tokens.filter(token => token.type === 'list').forEach(token => {
      delete token.lastNode;
      token.topLevelNodes.forEach(node => removeExtraKeys(node));
    });
  }

  function removeExtraKeys(node) {
    delete node.parentNode;
    delete node.indentation;
    node.children.forEach(removeExtraKeys);
  }
}

function extractTableCellAttributes(cellString) {
  let styles = [];
  let classNames = [];
  let text = cellString.replace(/(^|[^\\])\\style\s*=\s*"((?:\\.|[^"\\])*)"/g, (match, prefix, style) => {
    styles.push(style.replace(/\\(["\\])/g, '$1'));
    return prefix;
  });
  text = text.replace(/(^|[^\\])\\\.([A-Za-z0-9_-]+)/g, (match, prefix, className) => {
    classNames.push(className);
    return prefix;
  });

  return {
    text,
    style: styles.length ? styles.join('; ') : undefined,
    classNames: classNames.length ? classNames : undefined
  };
}

function TableToken(text, parserContext) {
  this.type = 'table';
  this.rows = [];
  const MERGE_DIRECTION = { 'v': {x: 0, y: 1}, '>': {x: 1, y: 0}, '<': {x: -1, y: 0}, '^': {x: 0, y: -1} };
  let cellObjectList = [];

  text.split("\n")
    .filter(Boolean) // the trailing newline will create an empty row
    .forEach((line, lineNumber) => {
      let trimmedCells = splitOnPipes(line).map(cell => cell.trim());
      let isDelimiterRow = trimmedCells.every(cell => cell === '' || cell.match(/^[=#-]+$/));
      if (isDelimiterRow) return; // ignore horizontal row marker

      let cellObjects = splitOnPipes(line).map(
        (cellString, cellIndex, cellsOfRow) => {
          if (cellString.match(/^\s*\\[v^<>]\s*$/)) return { tokens: [], merge: MERGE_DIRECTION[cellString.match(/[v^<>]/)[0]] };

          if (cellString.match(/^\s*\\x\s*$/)) return { tokens: [], hidden: true };

          let { text: cellText, style, classNames } = extractTableCellAttributes(cellString);

          let earlierCharactersOnLine = ['|'] // first | on line
            .concat(
              cellsOfRow.slice(0, cellIndex).join('|'), // all the intermediary cell contents plus pipes
              cellIndex > 0 ? '|' : '' // the pipe before the current cell
            ).join('');

          let cellObject = {
            tokens: parseText({
              text: cellText.trim(),  // we trim because the person might be using spaces to line up unevenly sized cells
              parserContext: parserContext.clone({
                insideToken: true,
              })
                .incrementLineAndResetCharacterNumber(lineNumber) // we want error messages to know how far into the table we are
                .incrementCharacterNumber(earlierCharactersOnLine.length + cellString.match(/^\s+/)?.[0].length) // count earlier chars and leading space
            })
          };

          if (style) cellObject.style = style;
          if (classNames) cellObject.classNames = classNames;
          return cellObject;
        }
      );
      this.rows.push(cellObjects);
    });

  if (detectTextDirection(text) === 'rtl') this.dir = 'rtl';

  this.rows.forEach((cellObjects, y, rows) => {
    cellObjects.forEach((cellObject, x, columns) => {
      if (cellObject.merge) {
        let collection = cellObject.merge.x ? columns : rows;
        let op = cellObject.merge;
        let dir = op.x ? 'x' : 'y';
        let attribute = cellObject.merge.x ? 'colspan' : 'rowspan';
        let limit = Math.max([op.x, op.y]) > 0 ? collection.length : -1;
        let selection = {x: x + op.x, y: y + op.y};
        if ((selection.x < 0 || selection.x > limit && limit > 0 && dir ==='x')
          ||(selection.y < 0 || selection.y > limit && limit > 0 && dir ==='y')) {
          throw new Error(chalk.red(`Invalid merge instructions in table: ${text}`));
        }

        while(selection[dir] !== limit) {
          if (!rows[selection.y][selection.x].merge) {
            rows[selection.y][selection.x][attribute] = (Number(rows[selection.y][selection.x][attribute]) || 1) + 1;
            break;
          } else {
            if (rows[selection.y][selection.x].merge.x !== op.x || rows[selection.y][selection.x].merge.y !== op.y) {
              throw new Error(chalk.red(`Invalid merge instructions in table: ${text}`)); // eg | \> | \^ |
            }
            selection[dir] += op[dir];
          }
        }
        cellObjectList.push(cellObject); // we preserve the direction information for the above check until we are done will all the cells
      }
    });
  });

  cellObjectList.forEach(cellObject => cellObject.merge && (cellObject.merge = true));
}

function MenuToken(text, parserContext) {
  this.type = 'menu';

  let items = [...text.matchAll(/(?:- ?|(([\w\d]{1,4})\.\s)|([<>]) )([^\n]+)\n/g)];

  const allRightAligned = items.every(item => item[3] === '>');
  const allLeftAligned = items.every(item => item[3] === '<');
  const mixedAlignment = items.some(item => item[3] === '<') && items.some(item => item[3] === '>');

  this.alignment = allRightAligned ? 'right' : (allLeftAligned ? 'left' : (mixedAlignment ? 'mixed' : null));

  items = items.map(item => {
    if (item[2]) { // It has an ordinal, so it's some sort of list.
      return { list: true, ordinal: item[2], text: item[4] };
    } else if (item[3]) { // It starts with '>' or '<', so it's a left or right aligned list item.
      return { list: false, text: item[4], alignment: item[3] === '>' ? 'right' : 'left' };
    } else { // It's not an ordinal item.
      return { list: false, text: item[4] };
    }
  });

  this.rtl = (text.startsWith('<') || detectTextDirection(text) === 'rtl') ? true : false;

  this.items = items.map((item, lineNumber) => {
    return {
      list: item.list,
      hidden: item.text.includes('\\x'),
      ordinal: item.ordinal,
      alignment: this.alignment === 'mixed' ? item.alignment : undefined,
      tokens: parseText({
        text: item.text.trim(),  // we trim because the person might be using spaces to line up unevenly sized cells
        parserContext: parserContext.clone({
          insideToken: true,
        })
          .incrementLineAndResetCharacterNumber(lineNumber + 1) // how far into the menu are we, line number plus initial delimiter
          .incrementCharacterNumber('- '.length) // count earlier chars and leading space
      })
    };
  });
}

function FootnoteLinesToken(text, parserContext) {
  this.type = 'footnote_lines';
  this.lines = [];

  let matches = Array.from(text.matchAll(/^\[\^([^\]]+)]:([^\n]*($|\n))/msg));

  matches.forEach(match => {
    let superscript = match[1];
    let text = match[2];
    let footnoteTokens = parseText({
      text,
      parserContext: parserContext.clone({
        insideToken: true }).incrementCharacterNumber('['+ superscript + ']:'.length)
    });

    this.lines.push({
      superscript,
      tokens: footnoteTokens
    });
  });
}

function CenterBlockToken(text, parserContext) {
  this.type = 'center_block';

  // parse inner text normally
  let innerTokens = parseText({
    text,
    parserContext: parserContext.clone({
      insideToken: true
    })
  });

  this.tokens = innerTokens;
}

function ItalicsToken(text, parserContext) {
  this.type = 'italics';
  this.tokens = parseText({
    text,
    parserContext: parserContext.clone({ insideToken: true }).incrementCharacterNumber('_'.length)
  });
}

function BoldToken(text, parserContext) {
  this.type = 'bold';
  this.tokens = parseText({
    text,
    parserContext: parserContext.clone({ insideToken: true }).incrementCharacterNumber('*'.length)
  });
}

function InlineCodeSnippetToken(text) {
  this.type = 'inline_code';
  this.text = text.split('\\\\').map(s => s.replace('\\', '')).join('\\'); // remove single backslashes, and replace double with single;
}

function UnderlineToken(text, parserContext) {
  this.type = 'underlined';
  this.tokens = parseText({
    text,
    parserContext: parserContext.clone({ insideToken: true }).incrementCharacterNumber('~'.length)
  });
}

function ToolTipToken(text, openingBrace, parserContext) {
  this.type = 'tool_tip';
  this.tokens = parseText({
    text,
    parserContext: parserContext.clone({ 
      insideToken: true, 
      noLinks: true
    }).incrementCharacterNumber(openingBrace.length) //'{!' or '{! '
  });
}

module.exports = {
  LocalReferenceToken,
  GlobalReferenceToken,
  DisabledReferenceToken,
  FragmentReferenceToken,
  TextToken,
  ExternalLinkToken,
  ImageToken,
  FootnoteMarkerToken,
  HtmlToken,
  CodeBlockToken,
  BlockQuoteToken,
  ListToken,
  TableToken,
  MenuToken,
  FootnoteLinesToken,
  ItalicsToken,
  BoldToken,
  InlineCodeSnippetToken,
  ToolTipToken,
  UnderlineToken,
  CenterBlockToken
};
